'use client'

import { FC } from 'react'
import styled from 'styled-components'

import { Button, Carat, RotateClockwise, TypeStyle, fromMd } from '@syconium/little-miss-figgy'

import { clearLoopParams, getLoopParams } from '../../../lib/adapters/loop/loopStorage'
import { formatPrettyCurrency } from '../../../lib/utils'
import { parseBoldMarkup } from '../../../lib/utils/translations'
import { useLocalization } from '../../_providers/LocalizationProvider.client'
import { useLoop } from '../../_providers/LoopProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'

export const LoopBanner: FC = () => {
  const { inLoopFlow } = useLoop()

  // `loop_total` is in "cents" (13800 = $138.00 in USD)
  const { loop_total, loop_redirect_url, loop_currency } = getLoopParams()
  const { locale } = useLocalization()
  const {
    loop: { amountInCredit, backToReturn },
  } = useTranslation()

  // if we don't have the total or redirect, we can't display the banner with full features
  // `loop_currency` *should* be USD, so if that is somehow missing we can just make the assumption.
  if (!inLoopFlow || !isNonEmptyString(loop_total) || !isNonEmptyString(loop_redirect_url)) {
    return null
  }

  const navigateToLoop = () => {
    clearLoopParams()
    window.location.assign(`https://${loop_redirect_url}`)
  }

  const returnCreditDisplay = formatPrettyCurrency({
    value: Number(loop_total),
    unit: 'cents',
    currency: loop_currency ?? 'USD', // Loop returns are just domestic, so `USD` is a valid fallback
    locale,
  })

  const returnCreditCopy = amountInCredit.replace('{{amount}}', returnCreditDisplay)

  return (
    <BannerBar>
      <BackToReturnButton
        variant='black-on-white-no-border'
        allCaps
        onClick={navigateToLoop}
        aria-label={backToReturn}
      >
        <IconWrapper>
          <Carat rotateClockwise={RotateClockwise.Half} aria-hidden='true' />
        </IconWrapper>
        <ButtonText>{backToReturn}</ButtonText>
      </BackToReturnButton>
      <CreditContainer>{parseBoldMarkup(returnCreditCopy, <CreditAmountCopy />)}</CreditContainer>
    </BannerBar>
  )
}

const isNonEmptyString = (value: string | null): value is string => {
  return !!value
}

const BannerBar = styled.aside`
  display: flex;
  padding: ${({ theme }) => theme.spacing(3, 4)};
  background: ${({ theme }) => theme.color.stroke.neutral.text.highContrast.on.background};
  justify-content: space-between;
  align-items: center;
  ${fromMd} {
    padding: ${({ theme }) => theme.spacing(3, 6)};
    justify-content: normal;
    align-self: stretch;
    gap: ${({ theme }) => theme.spacing(6)};
  }
`

// container for the spans
const CreditContainer = styled.div`
  color: ${({ theme }) => theme.color.text.primary.on.strong};
  text-align: center;
  ${TypeStyle.css.bodySmall};
  font-weight: 400;
`

// the $XX.XX portion of copy
const CreditAmountCopy = styled.span`
  font-weight: 700;
`

// the button to take users back to Loop
const BackToReturnButton = styled(Button)`
  display: flex;
  border: none;
  min-height: unset;
  padding: ${({ theme }) => theme.spacing(2, 4)};
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 16px;
  width: 16px;

  svg {
    display: block;
  }
`

const ButtonText = styled.span`
  letter-spacing: 1.6px;
  font-size: 11px;
  line-height: 1;
`
